/**
 * The app-wide agent panel: a non-modal sheet that rises from the bottom edge
 * of the viewport to just below the tab bar, inset from the right, with square corners
 * matching the app layout (the page stays usable behind it). The route
 * decides the assistant's focus: the client's on a client's pages, the
 * practice everywhere else, and nothing on a case (the assistant lives in
 * the IDE there). Opened from the header's `AgentTrigger`, a handoff, or a
 * `?agent=` link.
 * Its left edge drags to resize it (`panelWidth.ts`); the width is the
 * signed-in person's, per browser.
 */
import { Dialog as SheetPrimitive } from "radix-ui";
import { Building2 } from "lucide-react";
import { useEffect, useState, type FC } from "react";
import { useSelector } from "react-redux";

import { ResizeEdge } from "@/components/ui/resize-edge";
import { useTabBarBottom } from "@/features/appTabs/hooks/useTabBarBottom";
import { useViewportWidth } from "@/hooks/use-viewport-width";
import { useSidebarInsetLeft } from "../hooks/useSidebarInsetLeft";

import { lawyerSelectors } from "../../../redux/lawyer/selectors";
import {
  panelAgentLabel,
  panelWidthKey,
  useAgentPanelStore,
  usePanelWidth
} from "../agentPanelStore";
import {
  canWidenPanel,
  clampPanelWidth,
  isWidePanel,
  PANEL_DEFAULT_WIDTH,
  snapPanelWidth,
  toggledPanelWidth
} from "../panelWidth";
import { scopeKey } from "../types";
import { useAgentPanelScope } from "../useAgentPanelScope";
import { useAgentSpecs } from "../useAgentSpecs";
import { AgentPanelChat } from "./AgentPanelChat";
import { AgentPanelHeader, panelDescription } from "./AgentPanelHeader";
import {
  Sheet,
  SheetDescription,
  SheetTitle
} from "../../../components/ui/sheet";
import { cn } from "../../../lib/utils";

/** Clear of the tab bar by the same 12px the panel is inset from the right. */
const TOP_GAP = 12;

export const AgentPanel: FC = () => {
  const open = useAgentPanelStore((s) => s.open);
  const setOpen = useAgentPanelStore((s) => s.setOpen);
  const { agent, scope, loading, error } = useAgentPanelScope();
  const { specFor } = useAgentSpecs();
  const top = useTabBarBottom(open);
  const expanded = useAgentPanelStore((s) => s.expanded);
  const toggleExpanded = useAgentPanelStore((s) => s.toggleExpanded);
  const setExpanded = useAgentPanelStore((s) => s.setExpanded);
  const insetLeft = useSidebarInsetLeft(open && expanded);

  const uid = useSelector(lawyerSelectors.selectUid);
  const storedWidth = usePanelWidth(uid);
  const setStoredWidth = useAgentPanelStore((s) => s.setWidth);
  const viewport = useViewportWidth();
  const [dragWidth, setDragWidth] = useState<number | null>(null);
  const width = dragWidth ?? clampPanelWidth(storedWidth, viewport);
  const saveWidth = (next: number) => setStoredWidth(panelWidthKey(uid), next);
  const toggleWidth = canWidenPanel(viewport)
    ? () => saveWidth(toggledPanelWidth(width, viewport))
    : undefined;

  // Cases have no panel; one left open elsewhere closes on arrival.
  useEffect(() => {
    if (!agent && open) setOpen(false);
  }, [agent, open, setOpen]);

  if (!agent) return null;
  const spec = specFor("assistant");
  const label = panelAgentLabel(agent);
  const header = (
    <AgentPanelHeader
      label={label}
      expanded={expanded}
      onToggleExpand={toggleExpanded}
    />
  );

  return (
    <Sheet open={open} onOpenChange={setOpen} modal={false}>
      <SheetPrimitive.Portal>
        <SheetPrimitive.Content
          data-slot="sheet-content"
          data-side="bottom"
          data-testid="agent-panel"
          data-agent={agent}
          // Non-modal: clicking the page must not close the panel.
          onInteractOutside={(e) => e.preventDefault()}
          onOpenAutoFocus={(e) => e.preventDefault()}
          // Escape first restores an expanded chat; the next one closes it.
          onEscapeKeyDown={(e) => {
            if (!expanded) return;
            e.preventDefault();
            setExpanded(false);
          }}
          data-expanded={expanded ? "" : undefined}
          // Expanded: from the app sidebar's edge to the right inset, the
          // transcript and composer staying in their centered column.
          style={
            expanded
              ? { top: top + TOP_GAP, left: insetLeft + TOP_GAP }
              : { top: top + TOP_GAP, width }
          }
          className={cn(
            "fixed right-3 bottom-0 z-40 flex max-w-[calc(100vw-1.5rem)] flex-col overflow-hidden border border-b-0 border-line-2 bg-surface text-base text-fg shadow-dialog",
            // The close runs long enough for Casey to duck out of its header.
            "data-open:animate-in data-open:fade-in-0 data-open:slide-in-from-bottom-4 data-closed:animate-out data-closed:fade-out-0 data-closed:slide-out-to-bottom-4 data-closed:duration-200"
          )}
        >
          <SheetTitle className="sr-only">{label}</SheetTitle>
          <SheetDescription className="sr-only">
            {panelDescription(label)}
          </SheetDescription>
          {expanded ? null : (
            <ResizeEdge
              width={width}
              onDrag={setDragWidth}
              onCommit={saveWidth}
              clamp={(w) => clampPanelWidth(w, viewport)}
              snap={snapPanelWidth}
              defaultWidth={PANEL_DEFAULT_WIDTH}
              label="Resize Panel"
              testId="agent-panel-resize"
            />
          )}

          {error ? (
            <>
              {header}
              <div className="m-3 rounded-md bg-danger-soft px-3 py-2 text-sm text-danger">
                {error}
              </div>
            </>
          ) : scope ? (
            <AgentPanelChat
              key={scopeKey(scope)}
              agent={agent}
              scope={scope}
              spec={spec}
              wide={isWidePanel(width, viewport)}
              onToggleWidth={toggleWidth}
              expanded={expanded}
              onToggleExpand={toggleExpanded}
            />
          ) : loading ? (
            <>
              {header}
              <div className="flex flex-1 items-center justify-center text-sm text-fg-3">
                Loading…
              </div>
            </>
          ) : (
            <>
              {header}
              <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
                <Building2 className="size-6 text-fg-3" />
                <p className="max-w-64 text-sm text-fg-3">
                  Your account isn't attached to a practice, so the Practice
                  Manager has nothing to report on.
                </p>
              </div>
            </>
          )}
        </SheetPrimitive.Content>
      </SheetPrimitive.Portal>
    </Sheet>
  );
};
